import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/Auth';
import { supabase } from '../supabase';
import Datetime from 'react-datetime';
import "react-datetime/css/react-datetime.css";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import { Grid } from "react-loader-spinner";
import { useTranslation } from 'react-i18next';

export default function ChitForm({ history, match }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const chitId = match.params.chitId;
  const isAddMode = !chitId;

  // form validation rules
  const validationSchema = yup.object().shape({
    chitName: yup.string()
      .required('Chit name is required'),
    totalMembers: yup.number()
      .required('Total members count is required')
      .positive(),
    perPersonAmount: yup.number()
      .required('Per person amount is required')
      .positive(),
    auctionInterval: yup.number()
      .required('Auction internal months is required')
      .positive()
      .min(1, 'Auction internal must be at least 1 month'),
    firstAuctionDate: yup.date() // TODO: verify if we need to mandate this
  });

  // functions to build form returned by useForm() hook
  const { control, register, formState: { errors, isSubmitting }, handleSubmit, reset, setValue } = useForm({
    resolver: yupResolver(validationSchema)
  });

  const [, setChit] = useState({});
  const [hasAuctions, setHasAuctions] = useState(false);
  const [loadingChit, setLoadingChit] = useState(false);
  const [addChitError, setAddChitError] = useState("");
  useEffect(() => {
    const fetchChit = async (id) => {
      setLoadingChit(true);

      // fetch chit detail for Edit view
      let { data: chit, error } = await supabase
        .from("chits")
        .select("*")
        .eq('id', id)
        .single();
      if (error) {
        console.log("No records found");
        console.log("error", error);
      } else {
        const fields = {
          'chitName': 'chit_name',
          'totalMembers': 'total_members',
          'perPersonAmount': 'per_person_amount',
          'auctionInterval': 'auction_interval',
          'firstAuctionDate': 'first_auction_date'
        };
        for (const field in fields) {
          if (field === 'firstAuctionDate') {
            setValue(field, new Date(chit[fields[field]]));
          } else if (field === 'chitName') {
            setValue(field, chit[fields[field]]);
          } else {
            setValue(field, Number(chit[fields[field]]));
          }
        }
        setChit(chit);
      }

      // check if auctions were created to only allow
      // chitName change in Edit view
      let { data: auctions, auctionError } = await supabase
        .from("auctions")
        .select("*")
        .eq("chit_id", id);
      if (auctionError) {
        console.log("No past auctions found", auctionError);
        // TODO: End `loadingChit` and display the error(s)
      } else {
        console.log(`Fetched ${auctions.length} past auctions`);
        if (auctions && auctions.length > 0) {
          setHasAuctions(true);
        }
        setLoadingChit(false);
      }
    }

    if (!isAddMode) {
      // get the Chit data and set form fields
      fetchChit(chitId);
    }
  }, [isAddMode, chitId, setValue]);

  function onSubmit(data) {
    return isAddMode
      ? createChit(data)
      : updateChit(chitId, data);
  }

  const createChit = async (input) => {
    let { data: createdChit, error } = await supabase
      .from("chits")
      .insert({
        chit_name: input.chitName,
        total_members: input.totalMembers,
        per_person_amount: input.perPersonAmount,
        auction_interval: input.auctionInterval,
        first_auction_date: input.firstAuctionDate,
        next_auction_date: input.firstAuctionDate,
        next_installment_count: 1,
        user_id: user.id
      })
      .single();
    if (error) {
      console.log("Error while creating chit", error.message);
      setAddChitError(t('text.dataUpdateErrorGeneric'));
    } else {
      console.log("Chit saved successfully! Id: " + createdChit.id)
      history.push(`/chit/${createdChit.id}`)
    }
  }

  const updateChit = async (id, input) => {
    let dataToUpdate = {
      chit_name: input.chitName
    };
    if (!hasAuctions) {
      dataToUpdate['total_members'] = input.totalMembers;
      dataToUpdate['per_person_amount'] = input.perPersonAmount;
      dataToUpdate['auction_interval'] = input.auctionInterval;
      dataToUpdate['first_auction_date'] = input.firstAuctionDate;
      dataToUpdate['next_auction_date'] = input.firstAuctionDate;
    }
    let { data: updatedChit, error } = await supabase
      .from("chits")
      .update(dataToUpdate)
      .match({ id: id });
    if (error) {
      console.log("Error while updating chit", error.message);
      setAddChitError(t('text.dataUpdateErrorGeneric'));
    } else {
      console.log("Chit updated successfully! Id: " + id, updatedChit)
      history.push(`/chit/${id}`)
    }
  }

  let dateInputProps = {
    placeholder: t('label.firstAuctionDate'),
    className: "shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline rounded-md",
    disabled: hasAuctions
  }

  return (
    <div className="container flex flex-col mb-4">
      {
        loadingChit ?
          <div className="container flex px-4 py-4 mt-8 text-lg font-medium">
            <div className="mx-auto">
              <Grid type="Grid" color="#93C5FD" height="32" width="32" />
            </div>
          </div> :
          <div className="sm:w-2/4 w-3/4 mx-auto">
            <h4 className="text-lg font-extrabold mt-4 tracking-normal">
              {isAddMode ? t('button.addChit') : t('button.editChit')}
            </h4>
            <form className="shadow-md rounded-md px-8 pt-6 pb-8 mb-4 bg-blue-300 mt-4" onReset={reset} onSubmit={handleSubmit(onSubmit)}>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="chit-name">
                  {t('label.chitName')}
                </label>
                <input
                  id="chit-name"
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline rounded-md"
                  type="text"
                  placeholder={t('label.chitName')}
                  name="chitName"
                  {...register("chitName")} />
                <span className="text-red-500 text-xs italic">
                  {errors.chitName?.message}
                </span>
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="total-members">
                  {t('label.totalMembers')}
                </label>
                <input
                  id="total-members"
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline rounded-md"
                  type="number"
                  placeholder={t('label.totalMembers')}
                  name="totalMembers"
                  {...register("totalMembers")}
                  disabled={hasAuctions} />
                <span className="text-red-500 text-xs italic">
                  {errors.totalMembers?.message}
                </span>
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="per-person-amount">
                  {t('label.perPersonAmount')}
                </label>
                <input
                  id="per-person-amount"
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline rounded-md"
                  type="number"
                  placeholder={t('label.perPersonAmount')}
                  name="perPersonAmount"
                  {...register("perPersonAmount")}
                  disabled={hasAuctions} />
                <span className="text-red-500 text-xs italic">
                  {errors.perPersonAmount?.message}
                </span>
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="auction-interval">
                  {t('label.auctionInterval')}
                </label>
                <input
                  id="auction-interval"
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline rounded-md"
                  type="number"
                  placeholder={t('label.auctionInterval')}
                  name="auctionInterval"
                  {...register("auctionInterval")}
                  disabled={hasAuctions} />
                <span className="text-red-500 text-xs italic">
                  {errors.auctionInterval?.message}
                </span>
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="first-auction-date">
                  {t('label.firstAuctionDate')}
                </label>
                <Controller
                  control={control}
                  name="firstAuctionDate"
                  id="first-auction-date"
                  render={({ field }) => (
                    <Datetime
                      timeFormat={false}
                      closeOnSelect={true}
                      inputProps={dateInputProps}
                      onChange={(date) => field.onChange(date)}
                      value={field.value} />
                  )} />
                <span className="text-red-500 text-xs italic">
                  {errors.firstAuctionDate?.message}
                </span>
              </div>

              {addChitError && <div className="text-red-600 text-s italic mb-2">{addChitError}</div>}
              <div className="flex">
                <button disabled={isSubmitting} className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md focus:outline-none focus:shadow-outline mr-4" type="submit">
                  {t('button.saveChit')}
                </button>
                {isSubmitting && <span className="my-auto"><Grid type="Grid" color="#F9FAFB" height="28" width="28" /></span>}
              </div>
            </form>
          </div>
      }
    </div>
  );
}
